import type { RespuestaDeAthena } from './tipos.js';
import { jwtActual } from './auth.js';

/**
 * El cliente del API, con `credentials: 'include'` en todas las llamadas.
 *
 * Sin la cookie el API responde 403 y no llama a ningún modelo, así que la web pide la sesión al
 * cargar. Es same-origin por el rewrite `/api/*` de F0: no hay CORS que negociar.
 */
export const BASE = '/api';

export type ErrorDeAthena =
  | 'sin_sesion'
  | 'peticion_invalida'
  | 'cuota'
  | 'tope_de_gasto'
  | 'tope_de_gasto_anonimo'
  | 'no_disponible'
  | 'captcha_invalido'
  | 'red'
  | 'desconocido';

export interface FalloDeAthena {
  readonly tipo: ErrorDeAthena;
  /** Segundos que pide esperar el servidor, cuando los dice. */
  readonly reintentarEn: number | null;
  readonly cuotaAnonimaAgotada?: boolean;
}

/** Comparte la petición inicial para que una consulta rápida espere a recibir la cookie. */
let sesionPendiente: Promise<boolean> | null = null;
export function pedirSesion(): Promise<boolean> {
  if (sesionPendiente !== null) return sesionPendiente;
  sesionPendiente = fetch(`${BASE}/sesion`, { credentials: 'include' })
    .then(r => r.ok)
    .catch(() => false)
    .then(ok => {
      if (!ok) sesionPendiente = null;
      return ok;
    });
  return sesionPendiente;
}

export interface Consulta {
  readonly texto: string;
  /** `auto`: el modo lo elige Athena según la pregunta (MODO-01). */
  readonly modo: 'preguntar' | 'contrastar' | 'comparar' | 'auto';
  readonly idioma: 'es' | 'en';
  readonly conversacionId: string | null;
}

export type Resultado = { readonly ok: true; readonly respuesta: RespuestaDeAthena } | { readonly ok: false; readonly fallo: FalloDeAthena };

/** Las fases que avisa el motor (STREAM-01, `ResponderPreguntaService`), en su orden. */
export const FASES_DE_PROGRESO = ['buscando', 'leyendo', 'redactando', 'comprobando'] as const;
export type FaseDeProgreso = (typeof FASES_DE_PROGRESO)[number];
/** Un aviso de progreso: el texto que manda el motor y, si viene y es conocida, su fase. */
export interface AvisoDeProgreso {
  readonly texto: string;
  readonly fase: FaseDeProgreso | null;
}

/**
 * CAPTCHA-01. `captchaToken` lo trae ya resuelto quien llama (el widget vive en `Caja`, montado
 * antes de enviar, no se pide aquí bajo demanda): es más simple que la web decida cuándo hace falta
 * un captcha; el backend ya sabe si toca pedirlo (sesión de cuenta o cookie de 24 h lo eximen) y
 * simplemente ignora el token si no lo necesita.
 *
 * STREAM-01 (plan B, 2026-09-29): con `alProgresar`, la pregunta lleva un id propio en
 * `X-Athena-Progreso` y, mientras se espera la respuesta (el JSON de siempre), se piden las fases
 * a `GET /consulta/progreso/:id` cada segundo. Antes iban por SSE, pero el rewrite `/api/*` del
 * sitio estático de Render retiene la respuesta entera y las fases llegaban todas al final.
 * **Degradación**: si esas peticiones fallan o no traen nada, no pasa nada; la espera genérica
 * sigue y la respuesta llega igual.
 */
export async function preguntar(
  consulta: Consulta,
  captchaToken: string | null = null,
  alProgresar?: (aviso: AvisoDeProgreso) => void,
): Promise<Resultado> {
  if (!(await pedirSesion())) return { ok: false, fallo: { tipo: 'red', reintentarEn: null } };
  const idDeProgreso = alProgresar === undefined ? null : crypto.randomUUID();
  const sondeo = idDeProgreso === null || alProgresar === undefined ? null : sondearProgreso(idDeProgreso, alProgresar);
  // ADMIN-03: con sesión iniciada, la pregunta va con el JWT, para que el API sepa de qué cuenta es
  // (nivel, cuota, y `/admin` si aceptó los términos que lo dicen). Sin sesión, como siempre.
  const jwt = await jwtActual().catch(() => null);
  let r: Response;
  try {
    r = await fetch(`${BASE}/consulta/preguntas`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(idDeProgreso === null ? {} : { 'x-athena-progreso': idDeProgreso }),
        ...(jwt === null ? {} : { authorization: `Bearer ${jwt}` }),
      },
      body: JSON.stringify(captchaToken === null ? consulta : { ...consulta, captchaToken }),
    });
  } catch {
    return { ok: false, fallo: { tipo: 'red', reintentarEn: null } };
  } finally {
    sondeo?.parar();
  }

  if (r.ok) return { ok: true, respuesta: (await r.json()) as RespuestaDeAthena };
  const cuerpoError = (await r
    .clone()
    .json()
    .catch(() => null)) as { error?: { mensaje?: string } } | null;
  return {
    ok: false,
    fallo: {
      tipo: tipoDelFallo(r.status, await codigoDelError(r)),
      reintentarEn: leerRetryAfter(r),
      cuotaAnonimaAgotada:
        r.status === 429 && cuerpoError?.error?.mensaje === 'Has agotado las preguntas gratuitas de hoy. Regístrate para tener más.',
    },
  };
}

/** Cada cuánto se piden las fases mientras se espera la respuesta. */
export const MS_ENTRE_SONDEOS = 1000;

/** Lo mínimo que se ve una fase antes de la siguiente cuando llegan varias en el mismo sondeo. */
export const MS_POR_FASE_EN_COLA = 600;

/**
 * Pide las fases de la pregunta `id` cada segundo y avisa las nuevas, hasta que se le diga que pare.
 * Un fallo en una petición no corta nada: la siguiente lo vuelve a intentar.
 *
 * Si en un mismo sondeo llegan varias fases (leer y redactar suelen caer en el mismo segundo), no
 * se avisan de golpe: van en cola, cada una al menos `MS_POR_FASE_EN_COLA`, para que se vea cada
 * viñeta y la anterior pase a hecha. Al parar (ha llegado la respuesta), la cola se descarta: la
 * respuesta nunca espera a la animación.
 */
function sondearProgreso(id: string, alProgresar: (aviso: AvisoDeProgreso) => void): { parar: () => void } {
  let vistos = 0;
  let parado = false;
  let temporizador: ReturnType<typeof setTimeout> | undefined;
  const cola: AvisoDeProgreso[] = [];
  let entrega: ReturnType<typeof setTimeout> | undefined;
  let ultimaEntrega = -Infinity;

  const entregar = () => {
    entrega = undefined;
    if (parado) return;
    const siguiente = cola.shift();
    if (siguiente === undefined) return;
    alProgresar(siguiente);
    ultimaEntrega = Date.now();
    if (cola.length > 0) entrega = setTimeout(entregar, MS_POR_FASE_EN_COLA);
  };
  const encolar = (aviso: AvisoDeProgreso) => {
    cola.push(aviso);
    if (entrega !== undefined) return;
    entrega = setTimeout(entregar, Math.max(0, ultimaEntrega + MS_POR_FASE_EN_COLA - Date.now()));
  };

  const pedir = async () => {
    try {
      const r = await fetch(`${BASE}/consulta/progreso/${encodeURIComponent(id)}`, { credentials: 'include' });
      const { avisos } = r.ok ? ((await r.json()) as { avisos?: { texto?: unknown; fase?: unknown }[] }) : { avisos: [] };
      for (const a of (avisos ?? []).slice(vistos)) {
        if (parado || typeof a.texto !== 'string') continue;
        encolar({ texto: a.texto, fase: FASES_DE_PROGRESO.find(f => f === a.fase) ?? null });
      }
      vistos = Math.max(vistos, avisos?.length ?? 0);
    } catch {
      // Sin fases esta vez; la espera genérica sigue y se vuelve a pedir.
    }
    if (!parado) temporizador = setTimeout(() => void pedir(), MS_ENTRE_SONDEOS);
  };
  temporizador = setTimeout(() => void pedir(), MS_ENTRE_SONDEOS);
  return {
    parar: () => {
      parado = true;
      cola.length = 0;
      clearTimeout(temporizador);
      clearTimeout(entrega);
    },
  };
}

/**
 * Una respuesta ya guardada: la propia de esta sesión o la de una prediseñada. `null` si el API no
 * la da (otra sesión, id que no existe) o si no hay red; la pantalla dice lo mismo en los dos casos.
 */
export async function obtenerRespuesta(id: string): Promise<RespuestaDeAthena | null> {
  try {
    if (!(await pedirSesion())) return null;
    const r = await fetch(`${BASE}/consulta/preguntas/${encodeURIComponent(id)}`, { credentials: 'include' });
    if (!r.ok) return null;
    return (await r.json()) as RespuestaDeAthena;
  } catch {
    return null;
  }
}

/**
 * CONV-01: la conversación de esa respuesta, de esta sesión, para pintarla entera al recargar.
 * Vacía si no es de esta sesión (una prediseñada) o si no hay red: entonces se pide la suelta.
 */
export async function obtenerHilo(id: string): Promise<readonly { readonly texto: string; readonly respuesta: RespuestaDeAthena }[]> {
  try {
    if (!(await pedirSesion())) return [];
    const r = await fetch(`${BASE}/consulta/preguntas/${encodeURIComponent(id)}/hilo`, { credentials: 'include' });
    if (!r.ok) return [];
    return ((await r.json()) as { turnos: readonly { texto: string; respuesta: RespuestaDeAthena }[] }).turnos;
  } catch {
    return [];
  }
}

export interface PreguntaDeInicio {
  readonly id: string;
  readonly modo: 'preguntar' | 'contrastar' | 'comparar';
  readonly texto: string;
  readonly respuestaId: string;
  /** El lote con el que se generó su respuesta (2026-09-28). */
  readonly generadaConLote?: string | null;
}

/** Las preguntas de la portada, cada una con su respuesta ya calculada. Sin red, ninguna. */
export async function pedirPreguntasDeInicio(idioma: 'es' | 'en'): Promise<readonly PreguntaDeInicio[]> {
  try {
    const r = await fetch(`${BASE}/consulta/sugerencias?idioma=${idioma}`, { credentials: 'include' });
    if (!r.ok) return [];
    return ((await r.json()) as { sugerencias: readonly PreguntaDeInicio[] }).sugerencias;
  } catch {
    return [];
  }
}

/**
 * Avisa de que el lector copió la frase o abrió una fuente (G-5A4). **No espera ni falla**: medir no
 * puede estorbar a quien lee, así que un error de red se ignora.
 */
export function anotarEvento(tipo: 'copia' | 'clic_fuente', preguntaId: string): void {
  void fetch(`${BASE}/consulta/eventos`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tipo, preguntaId }),
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * Cada código dice una cosa distinta y quien pregunta merece saber cuál.
 *
 * "Ha fallado algo" para los cuatro casos es exactamente lo que impide que alguien entienda que
 * tiene que esperar un minuto y no cerrar la pestaña.
 */
/**
 * **Un 503 no siempre es el tope de gasto.** La API devuelve 503 también cuando el proveedor de
 * modelos no responde, y hasta el 2026-09-23 la web decía «vuelve mañana» en los dos casos (lo vio
 * la revisión de Astra de F5). Lo que los distingue es el `codigo` del cuerpo; un 503 sin él (el
 * propio Render, por ejemplo) tampoco es el tope.
 */
export function tipoDelFallo(estado: number, codigo?: string): ErrorDeAthena {
  if (estado === 400) return 'peticion_invalida';
  if (estado === 403) return codigo === 'CAPTCHA_INVALIDO' ? 'captcha_invalido' : 'sin_sesion';
  if (estado === 429) return 'cuota';
  if (estado === 503) {
    if (codigo === 'TOPE_GASTO') return 'tope_de_gasto';
    if (codigo === 'TOPE_GASTO_ANONIMO') return 'tope_de_gasto_anonimo';
    return 'no_disponible';
  }
  return 'desconocido';
}

/** El `codigo` de `{ error: { codigo } }`, o nada si el cuerpo no es eso. */
async function codigoDelError(r: Response): Promise<string | undefined> {
  try {
    const cuerpo = (await r.json()) as { error?: { codigo?: unknown } } | null;
    return typeof cuerpo?.error?.codigo === 'string' ? cuerpo.error.codigo : undefined;
  } catch {
    return undefined;
  }
}

export function leerRetryAfter(r: Response): number | null {
  const cabecera = r.headers.get('retry-after');
  if (cabecera === null) return null;
  const segundos = Number(cabecera);
  return Number.isFinite(segundos) && segundos > 0 ? Math.ceil(segundos) : null;
}
